import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  BackHandler,
  Dimensions,
  Keyboard,
  PixelRatio,
  Platform,
  View,
  TouchableOpacity,
} from 'react-native';
import Animated, {
  Easing,
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import styled from 'styled-components/native';
import {hdp as staticHdp, isWeb} from '../../utils/responsive';
import useKeyboardStatus from '../../utils/hooks/useKeyboardStatus';
import Icon from '../../components/icon';
import {tr} from '../../translations';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import TextInput from '../../components/textInput';
import {searchAndFilterLanguages} from '../../utils/language';
import {
  LanguageItemType,
  LanguagePickerModalProps,
} from '../../interfaces/connected-components/languagePickerModal';
import {closeLanguagePickerModal} from './actions';
import LanguageListItem from '../../components/languageListItem';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import {languagesData} from '../../assets/static/languages';

const screenHeight = Dimensions.get('screen').height;
const modalHeight = staticHdp(90);
const separatorHeight = 28 / PixelRatio.get();

export const languageListHeaderHeight = 252 / PixelRatio.get();
export const languageListItemHeight = 168 / PixelRatio.get();

// Web-specific modal styles
const WebOverlayContainer = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10001;
`;

const WebModalContainer = styled.View<{responsiveWidth: number}>`
  width: ${({responsiveWidth}) => responsiveWidth}px;
  max-height: 80vh;
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 8px;
  overflow: hidden;
  shadow-color: #000;
  shadow-offset: 0px 5px;
  shadow-opacity: 0.2;
  shadow-radius: 15px;
  elevation: 10;
  ${isWeb ? 'box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.2);' : ''}
`;

const WebHeaderRow = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom-width: 2px;
  border-bottom-color: ${({theme}) => theme.border + '33'};
  background-color: ${({theme}) => theme.homeBackground};
`;

const WebCloseButton = styled.TouchableOpacity`
  padding: 8px;
`;

// Mobile-specific styles remain unchanged
const ModalView = styled.View`
  flex: 1;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
`;
const Overlay = styled(Animated.View)`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
`;
const ContentContainer = styled.View<{
  responsiveWidth: number;
  responsiveHeight: number;
}>`
  border-radius: 16px;
  overflow: hidden;
  background-color: ${({theme}) => theme.homeBackground};
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0px 2px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 4;
  width: ${({responsiveWidth}) => responsiveWidth}px;
  height: ${({responsiveHeight}) => responsiveHeight}px;
  align-self: center;
  margin-top: auto;
  z-index: 10001;
`;
const BackIconContainer = styled.View`
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  margin-right: 5%;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0px 2px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 4;
`;
const Separator = styled.View`
  height: ${separatorHeight}px;
`;
const HeaderContainer = styled(Animated.View)`
  position: absolute;
  top: 0;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-horizontal: 4%;
  background-color: ${({theme}) => theme.homeBackground};
  height: ${languageListHeaderHeight}px;
  z-index: 999;
`;

const LanguagePickerModal = ({
  languagePickerModal,
}: LanguagePickerModalProps) => {
  const isKeyboardOpen = useKeyboardStatus();
  const theme = useTheme() as Theme;
  const {getByScreenSize, hdp, wdp, isMobile} = useResponsiveDimensions();
  const {
    isVisible,
    onSelectionChange,
    currentValue,
    filteredLanguageCodes,
    withSearch,
  } = languagePickerModal || {};
  const [modalVisible, setModalVisible] = useState(false);
  const [search, setSearch] = useState('');
  const [languages, setLanguages] = useState<LanguageItemType[]>([]);
  const [searchLanguages, setSearchLanguages] = useState<LanguageItemType[]>(
    [],
  );
  const overlayOpacity = useSharedValue(0);
  const translateY = useSharedValue(0);
  const scale = useSharedValue(0);
  const duration = isVisible ? 700 : 800;
  const headerAnimation = useSharedValue(1);
  const previousScrollY = useSharedValue(1);
  const flatListRef = useRef<Animated.FlatList<any>>(null);
  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      const currentScrollY = Math.abs(event.contentOffset.y);
      headerAnimation.value = withTiming(
        event.contentOffset.y < languageListHeaderHeight ||
          (currentScrollY > previousScrollY.value
            ? currentScrollY - previousScrollY.value
            : previousScrollY.value - currentScrollY) <
            languageListItemHeight / 2 ||
          currentScrollY < previousScrollY.value
          ? 1
          : 0,
      );
    },
    onBeginDrag: event => {
      previousScrollY.value = Math.abs(event.contentOffset.y);
    },
  });

  const headerStyle = useAnimatedStyle(() => {
    return {
      opacity: headerAnimation.value,
      transform: [
        {
          translateY: interpolate(
            headerAnimation.value,
            [0, 1],
            [-languageListHeaderHeight, 0],
          ),
        },
      ],
    };
  });

  const toggleModalAnimation = () => {
    overlayOpacity.value = withTiming(isVisible ? 1 : 0, {
      duration,
      easing: Easing.out(Easing.exp),
    });
    translateY.value = withTiming(isVisible ? 1 : 0, {
      duration,
      easing: Easing.out(Easing.exp),
    });
    scale.value = withTiming(isVisible ? 1 : 0, {
      duration,
      easing: Easing.out(Easing.exp),
    });
  };

  useEffect(() => {
    toggleModalAnimation();
    if (isVisible) {
      setModalVisible(true);
      if ((filteredLanguageCodes || [])?.length > 0) {
        initPicker(false);
      }
    } else {
      setTimeout(() => {
        setModalVisible(false);
        initPicker();
        flatListRef?.current?.scrollToOffset({offset: 0});
      }, duration);
    }
  }, [isVisible]);

  useEffect(() => {
    handleSearch();
  }, [search]);

  const initPicker = useCallback(
    (refresh: boolean = true) => {
      const results = searchAndFilterLanguages(
        '',
        refresh ? null : filteredLanguageCodes,
      );
      setSearchLanguages(results);
      setLanguages(results);
      setSearch('');
    },
    [filteredLanguageCodes],
  );

  // Handle back button press
  const handleBackPress = useCallback(() => {
    if (isVisible) {
      closeLanguagePickerModal();
      return true;
    }
    return false;
  }, [isVisible]);

  // Set up the back button listener
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const subscription = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => {
        subscription.remove();
      };
    }
  }, [handleBackPress]);

  const handleSearch = useCallback(() => {
    if (search.length === 0) {
      setSearchLanguages(languages);
      return;
    }
    const results = searchAndFilterLanguages(search, null, languages);
    setSearchLanguages(results);
  }, [search]);

  const submit = (item: LanguageItemType) => {
    onSelectionChange && onSelectionChange(item);
    closeLanguagePickerModal();
  };

  const overlayStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: `rgba(0, 0, 0, ${overlayOpacity.value * 0.3})`,
    };
  });

  const modalStyle = useAnimatedStyle(() => {
    const translate = interpolate(
      translateY.value,
      [0, 1],
      [screenHeight, screenHeight - modalHeight],
    );
    const scaleValue = interpolate(scale.value, [0, 1], [0.8, 1]);
    return {
      transform: [{translateY: translate}, {scale: scaleValue}],
    };
  });

  const _renderItem = ({item}: {item: LanguageItemType}) => {
    return (
      <LanguageListItem
        item={item}
        onPress={submit}
        currentValue={currentValue}
      />
    );
  };

  const _renderHeader = useCallback(() => {
    const isSearch = (search || '').trim().length > 0;
    return (
      <HeaderContainer style={headerStyle} key={isVisible + ''}>
        <BackIconContainer>
          <Icon
            name={'x'}
            color={theme.text.dark}
            size={theme.text.s55}
            role={'button'}
            onPress={closeLanguagePickerModal}
          />
        </BackIconContainer>
        {withSearch && (
          <TextInput
            value={search}
            onChangeText={text => setSearch(text)}
            placeholder={tr('languagePickerModal.searchPlaceholder')}
            showRightIcon
            rightIconName={isSearch ? 'search-off' : 'search'}
            rightIconType={'MaterialIcons'}
            onRightIconPress={isSearch ? () => setSearch('') : undefined}
            containerStyle={{
              flex: 1,
              alignSelf: 'center',
            }}
          />
        )}
      </HeaderContainer>
    );
  }, [search, withSearch, isVisible]);

  const _renderSeparator = useCallback(() => {
    return <Separator />;
  }, []);

  // Web-specific render function
  const renderWebContent = () => {
    return (
      <Animated.FlatList
        ref={flatListRef}
        onMomentumScrollBegin={() => isKeyboardOpen && Keyboard.dismiss()}
        data={searchLanguages}
        keyExtractor={item => item.code}
        renderItem={_renderItem}
        ItemSeparatorComponent={_renderSeparator}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: hdp(5),
          paddingTop: hdp(2),
        }}
        //@ts-ignore
        style={{
          maxHeight: '70vh',
          minHeight: '60vh',
        }}
        keyboardShouldPersistTaps={'handled'}
        automaticallyAdjustContentInsets={false}
        scrollEventThrottle={16}
        getItemLayout={(_data, index) => ({
          length: languageListItemHeight,
          offset: languageListItemHeight * index,
          index,
        })}
        initialNumToRender={15}
      />
    );
  };

  // Web-specific render
  if (isWeb && !isMobile) {
    if (!modalVisible) return null;
    return (
      <WebOverlayContainer>
        <Overlay
          onPointerDown={() => {
            isKeyboardOpen ? Keyboard.dismiss() : closeLanguagePickerModal();
          }}
        />
        <WebModalContainer responsiveWidth={getByScreenSize(wdp(95), wdp(60))}>
          <WebHeaderRow>
            <TextInput
              value={search}
              onChangeText={text => setSearch(text)}
              placeholder={tr('languagePickerModal.searchPlaceholder')}
              showRightIcon
              rightIconName={search ? 'search-off' : 'search'}
              rightIconType={'MaterialIcons'}
              onRightIconPress={search ? () => setSearch('') : undefined}
              containerStyle={{
                flex: 1,
                alignSelf: 'center',
                marginRight: 10,
              }}
            />
            <WebCloseButton onPress={closeLanguagePickerModal}>
              <Icon
                name={'close'}
                size={theme.text.s6}
                color={theme.secondary + 'AA'}
              />
            </WebCloseButton>
          </WebHeaderRow>
          {renderWebContent()}
        </WebModalContainer>
        <TouchableOpacity
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
          }}
          onPress={closeLanguagePickerModal}
          activeOpacity={1}
        />
      </WebOverlayContainer>
    );
  }

  if (isWeb && !modalVisible) return null;
  // Mobile render remains unchanged
  return (
    <ModalView pointerEvents={modalVisible ? 'auto' : 'none'}>
      <Overlay
        onTouchStart={() => {
          isKeyboardOpen ? Keyboard.dismiss() : closeLanguagePickerModal();
        }}
        style={overlayStyle}
      />
      <Animated.View style={modalStyle}>
        <ContentContainer responsiveWidth={wdp(95)} responsiveHeight={hdp(90)}>
          <Animated.FlatList
            ref={flatListRef}
            onMomentumScrollBegin={() => isKeyboardOpen && Keyboard.dismiss()}
            data={searchLanguages}
            keyExtractor={item => item.code}
            renderItem={_renderItem}
            ItemSeparatorComponent={_renderSeparator}
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: hdp(10),
              paddingTop: languageListHeaderHeight,
            }}
            onScroll={scrollHandler}
            keyboardShouldPersistTaps={'handled'}
            automaticallyAdjustContentInsets={false}
            scrollEventThrottle={5}
            getItemLayout={(_data, index) => ({
              length: languageListItemHeight,
              offset: languageListItemHeight * index,
              index,
            })}
            initialNumToRender={isWeb ? languagesData.length : 15}
          />
          {_renderHeader()}
        </ContentContainer>
      </Animated.View>
    </ModalView>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    languagePickerModal: state.languagePickerModal,
  };
};

export default connect(mapStateToProps)(LanguagePickerModal);
